import { recharts, ChartContainer, ChartConfig, Card, Empty, lucideIcons, Spinner } from '@round/creatorbase-ui';
import { PhylloUserAnalyticsData } from '@round/api';
import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import capitalize from 'lodash/capitalize';
import styles from './Audience.module.css';
import { toDecimalPoint } from '@round/utils';
import { Gender, GENDER_COLORS } from './helpers';
import cn from 'classnames';

const { PieChart, Pie } = recharts;
const { SearchXIcon, CircleAlertIcon } = lucideIcons;

type GenderAgeDistribution = PhylloUserAnalyticsData['profile']['audience']['gender_age_distribution'];
const emptyData: GenderAgeDistribution = [];

type Props = {
    data: GenderAgeDistribution;
    isLoading?: boolean;
    errorMessage: string | null;
};

const GenderChart = ({ data = emptyData, isLoading = false, errorMessage = null }: Props) => {
    const isError = !!errorMessage;

    const genders: Gender[] = useMemo(() => uniq(data.map((item) => item.gender.toLowerCase() as Gender)), [data]);
    const config: ChartConfig = useMemo(() => {
        return genders.reduce((acc, gender) => {
            acc[gender] = {
                label: capitalize(gender),
                color: GENDER_COLORS[gender as keyof typeof GENDER_COLORS],
            };
            return acc;
        }, {} as ChartConfig);
    }, [genders]);

    const chartData: { gender: Gender; value: number }[] = useMemo(() => {
        return genders.map((gender) => ({
            gender: gender,
            value: data.reduce((acc, item) => {
                if (item.gender.toLowerCase() === gender) {
                    return acc + item.value;
                }
                return acc;
            }, 0),
            fill: config[gender].color,
        }));
    }, [genders, data, config]);

    return (
        <Card size="sm" variant="borderless" className={cn(styles.chartContainer, styles.genderChart)}>
            <Card.Header className={styles.chartHeader}>
                <Card.Title>Gender</Card.Title>
            </Card.Header>
            <Card.Content className={styles.chartContent}>
                {isLoading && (
                    <div className={styles.spinnerContainer}>
                        <Spinner className={styles.spinner} />
                    </div>
                )}

                {!isLoading && chartData.length === 0 && (
                    <Empty>
                        <Empty.Media variant="icon">{isError ? <CircleAlertIcon /> : <SearchXIcon />}</Empty.Media>
                        <Empty.Title>{isError ? 'Something went wrong' : 'No data available'}</Empty.Title>
                        <Empty.Description>
                            {isError
                                ? errorMessage ?? 'Failed to load gender chart. Please try again.'
                                : 'Please try again later.'}
                        </Empty.Description>
                    </Empty>
                )}

                {!isLoading && chartData.length > 0 && (
                    <ChartContainer config={config} className={styles.chart}>
                        <PieChart>
                            <ChartContainer.Tooltip
                                cursor={false}
                                content={
                                    <ChartContainer.TooltipContent
                                        hideLabel
                                        formatter={(value, name) => {
                                            const label = config[name as Gender]?.label;
                                            const percentageText = value ? toDecimalPoint(Number(value), 2) : '-';
                                            return `${label}: ${percentageText}%`;
                                        }}
                                    />
                                }
                            />
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="gender"
                                innerRadius={55}
                                outerRadius={85}
                                strokeWidth={2}
                                stroke="var(--background)"
                            />

                            <ChartContainer.Legend
                                iconType="circle"
                                iconSize={10}
                                formatter={(value) => {
                                    const label = config[value]?.label;
                                    const percentage = chartData.find((data) => data.gender === value)?.value;
                                    const percentageText = percentage ? toDecimalPoint(percentage, 2) : '-';
                                    return (
                                        <span className={styles.percentageText}>
                                            {label} {percentageText}%
                                        </span>
                                    );
                                }}
                            />
                        </PieChart>
                    </ChartContainer>
                )}
            </Card.Content>
        </Card>
    );
};

export default GenderChart;
